import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../security";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;
const v2Enabled = process.env.REACT_APP_V2_ENABLED === "true";

export const apiFirmSlice = createApi({
  reducerPath: "apiFirm",
  tagTypes: ["ExcludedFirms"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      return headers;
    }
  }),

  endpoints: (builder) => ({
    getTimeForAttorneys: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/attorney_time"
            : `/employers/${id}/attorney_time`
        }`,
      transformResponse: (response: any) => response.time_for_attorneys_at_firm
    }),
    getJobStatistics: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/job_statistics"
            : `/employers/${id}/job_statistics`
        }`,
      transformResponse: (response: any) =>
        response.job_statistics.open_jobs_count
    }),
    getEmployerLocations: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/locations"
            : `/employers/${id}/locations`
        }`,
      transformResponse: (response: any) => response.locations
    }),
    getEmployerJDYearMedian: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/jd_year_median"
            : `/employers/${id}/jd_year_median`
        }`,
      transformResponse: (response: any) => response.jd_year_median
    }),
    getEmployerMostAttendedSchools: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/most_attended_schools"
            : `/employers/${id}/most_attended_schools`
        }`,
      transformResponse: (response: any) => response.most_attended_schools
    }),
    getEmployeesPerOffice: builder.query({
      query: (id) =>
        `${
          v2Enabled
            ? "/api/v2/employer_analytics/employee_per_office"
            : `/employers/${id}/employee_per_office`
        }`,
      transformResponse: (response: any) => response.employees_per_office
    }),
    getExcludedFirms: builder.query({
      query: () => "/api/v1/excluded_firms",
      providesTags: ["ExcludedFirms"]
    }),
    addExcludedFirm: builder.mutation({
      query: (firmId) => ({
        url: "/api/v1/excluded_firms/add",
        method: "POST",
        body: { employer_id: firmId }
      }),
      invalidatesTags: ["ExcludedFirms"]
    }),
    removeExcludedFirm: builder.mutation({
      query: (firmId) => ({
        url: "/api/v1/excluded_firms/remove",
        method: "DELETE",
        body: { employer_id: firmId }
      }),
      invalidatesTags: ["ExcludedFirms"]
    })
  }),

  refetchOnMountOrArgChange: true
});

// Export hooks for usage in functional components
export const {
  useGetTimeForAttorneysQuery,
  useGetJobStatisticsQuery,
  useGetEmployerLocationsQuery,
  useGetEmployerJDYearMedianQuery,
  useGetEmployerMostAttendedSchoolsQuery,
  useGetExcludedFirmsQuery,
  useGetEmployeesPerOfficeQuery,
  useRemoveExcludedFirmMutation,
  useAddExcludedFirmMutation
} = apiFirmSlice;
